/**
 * A deliberately small markdown renderer for the review screen.
 *
 * WHY NOT A LIBRARY
 *   The review screen renders a skill document written by an operator, and the
 *   renderer runs in the browser of the person deciding whether to approve it.
 *   That is the one place in this application where a rendering bug is a
 *   security bug. A library would bring a parser, a plugin surface and a
 *   sanitiser; this file brings a fixed grammar it fully understands, and
 *   escapes every character of the source before it looks at a single marker.
 *
 * THE ORDER MATTERS
 *   `escapeHtml` runs over the whole source first. Everything after that works
 *   on text in which `<`, `>` and `&` are already inert, so no branch of this
 *   renderer can introduce markup the author did not write, and no branch can
 *   fail to escape one. The only markup in the output is the small set below.
 *
 * WHAT IT SUPPORTS, AND NOTHING ELSE
 *   ATX headings, unordered lists, fenced code blocks, paragraphs, hard breaks,
 *   inline code, and links whose scheme is http or https. Raw HTML in the
 *   source is not interpreted at all; it is escaped and shown as text, which is
 *   the correct behaviour for a document that is meant to be instructions rather
 *   than a web page.
 */

import { escapeHtml, html, raw } from './html.js';

const FENCE = /^```/;

interface Block {
  readonly kind: 'heading' | 'paragraph' | 'list' | 'code';
  readonly level: number;
  readonly lines: readonly string[];
}

/**
 * Split a document into the block kinds this renderer understands.
 *
 * Grouping happens first and inline formatting second, because inline markers are
 * only meaningful inside a line and a code fence's contents must survive
 * untouched.
 */
function blocks(source: string): readonly Block[] {
  const out: Block[] = [];
  let index = 0;
  const lines = source.split('\n');

  while (index < lines.length) {
    const line = lines[index] ?? '';
    if (line.trim() === '') {
      index += 1;
      continue;
    }
    if (FENCE.test(line.trim())) {
      const body: string[] = [];
      index += 1;
      while (index < lines.length && !FENCE.test((lines[index] ?? '').trim())) {
        body.push(lines[index] ?? '');
        index += 1;
      }
      index += 1;
      out.push({ kind: 'code', level: 0, lines: body });
      continue;
    }
    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading !== null) {
      out.push({ kind: 'heading', level: (heading[1] ?? '#').length, lines: [heading[2] ?? ''] });
      index += 1;
      continue;
    }
    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (index < lines.length && /^\s*[-*]\s+/.test(lines[index] ?? '')) {
        items.push((lines[index] ?? '').replace(/^\s*[-*]\s+/, ''));
        index += 1;
      }
      out.push({ kind: 'list', level: 0, lines: items });
      continue;
    }
    const paragraph: string[] = [];
    while (index < lines.length) {
      const current = lines[index] ?? '';
      if (current.trim() === '' || FENCE.test(current.trim()) || /^(#{1,6})\s/.test(current)) {
        break;
      }
      if (/^\s*[-*]\s+/.test(current) && paragraph.length > 0) break;
      paragraph.push(current);
      index += 1;
    }
    out.push({ kind: 'paragraph', level: 0, lines: paragraph });
  }

  return out;
}

/** Inline markers, applied to a line that is already escaped. */
function inline(escapedLine: string): string {
  const pieces: string[] = [];
  let rest = escapedLine;

  // Inline code first: its contents must not be reinterpreted as other markers.
  for (;;) {
    const match = /`([^`]+)`/.exec(rest);
    if (match === null) break;
    const start = match.index;
    pieces.push(rest.slice(0, start));
    pieces.push(`<code>${match[1]}</code>`);
    rest = rest.slice(start + match[0].length);
  }

  // Then links, and only with a scheme that cannot execute anything.
  rest = rest.replace(/\[([^\]]*)\]\(([^)\s]+)\)/g, (whole, label: string, target: string) => {
    if (!/^https?:\/\//i.test(target)) return whole;
    return `<a href="${target}" rel="noopener noreferrer">${label}</a>`;
  });

  pieces.push(rest);
  return pieces.join('');
}

const HEADING_TAGS = ['h3', 'h4', 'h5', 'h6', 'h6', 'h6'] as const;

export function renderMarkdown(source: string): string {
  const escaped = escapeHtml(source);
  const out: string[] = [];
  for (const block of blocks(escaped)) {
    switch (block.kind) {
      case 'heading': {
        const tagName = HEADING_TAGS[Math.min(block.level, HEADING_TAGS.length) - 1] ?? 'h6';
        out.push(`<${tagName}>${inline(block.lines[0] ?? '')}</${tagName}>`);
        break;
      }
      case 'paragraph':
        out.push(html`<p>${raw(block.lines.map(inline).join('\n'))}</p>`.value);
        break;
      case 'list':
        out.push(
          html`<ul>${block.lines.map((item) => html`<li>${raw(inline(item))}</li>`)}</ul>`.value,
        );
        break;
      case 'code':
        out.push(html`<pre class="diff"><code>${block.lines.join('\n')}</code></pre>`.value);
        break;
    }
  }
  return out.join('');
}